import { useTranslation } from "@impero-com/common/utils/i18n";

import {
    DefaultOrCustomForDueDateOffsetForMonthOffsetPeriod as DefaultOrCustomOffset,
    HalfYear,
    Month,
    Quarter,
    TwoMonths,
} from "api/generated/model";
import { SourceToggleMode } from "components/markdownEditor/types";

export const CONTROL_DETAILS_ANCHOR = "control-details";
export const CONTROL_ASSIGNEES_ANCHOR = "control-assignees";
export const CONTROL_SCHEDULING_ANCHOR = "control-scheduling";
export const CONTROL_PLANNING_ANCHOR = "control-planning";
export const CONTROL_REMINDERS_ANCHOR = "control-reminders";
export const CONTROL_TASKS_ANCHOR = "control-tasks";

// The order MATTERS
export const CONTROL_ANCHORS = [
    CONTROL_DETAILS_ANCHOR,
    CONTROL_ASSIGNEES_ANCHOR,
    CONTROL_SCHEDULING_ANCHOR,
    CONTROL_PLANNING_ANCHOR,
    CONTROL_REMINDERS_ANCHOR,
    CONTROL_TASKS_ANCHOR,
] as const;

export type ControlAnchor = (typeof CONTROL_ANCHORS)[number];

export function useControlViewAnchorLabels(): Record<ControlAnchor, string> {
    const { t } = useTranslation();
    return {
        [CONTROL_DETAILS_ANCHOR]: t("EditControlProgram_Details"),
        [CONTROL_ASSIGNEES_ANCHOR]: t("CreateControl_Assignees_Title"),
        [CONTROL_SCHEDULING_ANCHOR]: t("CreateControl_Scheduling_Title"),
        [CONTROL_PLANNING_ANCHOR]: t("CreateControl_Planning_Title"),
        [CONTROL_REMINDERS_ANCHOR]: t("CreateControl_Reminders_Title"),
        [CONTROL_TASKS_ANCHOR]: t("CreateControl_Tasks_Title"),
    };
}

export const DEFAULT_MONTHLY_OCCURRENCE: Record<Month, DefaultOrCustomOffset> =
    {
        January: "Default",
        February: "Default",
        March: "Default",
        April: "Default",
        May: "Default",
        June: "Default",
        July: "Default",
        August: "Default",
        September: "Default",
        October: "Default",
        November: "Default",
        December: "Default",
    } as const;
export const ALL_MONTHS: Array<Month> = Object.keys(
    DEFAULT_MONTHLY_OCCURRENCE
) as Array<Month>;

export const DEFAULT_EVERY_2_MONTH_OCCURRENCE: Record<
    TwoMonths,
    DefaultOrCustomOffset
> = {
    JanuaryFebruary: "Default",
    MarchApril: "Default",
    MayJune: "Default",
    JulyAugust: "Default",
    SeptemberOctober: "Default",
    NovemberDecember: "Default",
} as const;
export const ALL_TWO_MONTHS: Array<TwoMonths> = Object.keys(
    DEFAULT_EVERY_2_MONTH_OCCURRENCE
) as Array<TwoMonths>;

export const DEFAULT_QUARTERLY_OCCURRENCE: Record<
    Quarter,
    DefaultOrCustomOffset
> = {
    First: "Default",
    Second: "Default",
    Third: "Default",
    Fourth: "Default",
} as const;
export const ALL_QUARTERS: Array<Quarter> = Object.keys(
    DEFAULT_QUARTERLY_OCCURRENCE
) as Array<Quarter>;

export const DEFAULT_HALF_YEARLY_OCCURRENCE: Record<
    HalfYear,
    DefaultOrCustomOffset
> = {
    First: "Default",
    Second: "Default",
} as const;
export const ALL_HALF_YEARS: Array<HalfYear> = Object.keys(
    DEFAULT_HALF_YEARLY_OCCURRENCE
) as Array<HalfYear>;

export const MARKDOWN_SOURCE_TOGGLE_MODES: Record<
    string,
    Record<number, SourceToggleMode>
> = {
    "testfoss.localhost": {
        // sp@impero.com
        2960: "SourceModeByDefault",
    },
    "testfoss.staging.next.imperocloud.dk": {
        // sp@impero.com
        2960: "SourceModeByDefault",
        // xy@impero.com
        3008: "SourceModeByDefault",
    },
    "testfoss.preproduction.rc.imperocloud.dk": {
        // xy@impero.com
        3008: "SourceModeByDefault",
        // sp@impero.com
        2960: "SourceModeByDefault",
    },
    "porsche-test.impero.com": {
        /// kresimir.matic@porsche.de
        9: "Visible",
        // oliver.kuhn@porsche.de
        112: "Visible",
        // gtni6t6@porsche.de
        122: "SourceModeByDefault",
        // rpa-3683@porsche.de
        154: "SourceModeByDefault",
    },
    "porsche.impero.com": {
        /// kresimir.matic@porsche.de
        3: "Visible",
        // oliver.kuhn@porsche.de
        308: "Visible",
        // gtni6t6@porsche.de
        323: "SourceModeByDefault",
        // rpa-3683@porsche.de
        1015: "SourceModeByDefault",
    },
};
